/**
 * AssetManager - Handles loading and management of game assets (models, textures, etc.)
 */
import * as THREE from 'three';
import { GLTFLoader } from 'three/examples/jsm/loaders/GLTFLoader.js';

export class AssetManager {
    constructor() {
        // Initialize asset collections
        this.textures = {};
        this.models = {};
        this.materials = {};
        this.audioBuffers = {};
        
        // Asset loading states
        this.totalAssets = 0;
        this.loadedAssets = 0;
        
        // Create loaders
        this.textureLoader = new THREE.TextureLoader();
        this.gltfLoader = new GLTFLoader();
        
        // Default material library for fallbacks
        this.defaultMaterials = {
            character: new THREE.MeshStandardMaterial({ color: 0x7777ff }),
            weapon: new THREE.MeshStandardMaterial({ color: 0xcccccc }),
            mount: new THREE.MeshStandardMaterial({ color: 0x995522 }),
            environment: new THREE.MeshBasicMaterial({ 
                color: 0x333366, 
                side: THREE.BackSide,
                wireframe: true
            })
        };
    }
    
    /**
     * Initialize asset manager and preload essential assets
     * @returns {Promise} Resolves when assets are loaded
     */
    async init() {
        console.log('Initializing AssetManager...');
        
        // Setup loading progress indicator
        const loadingProgressBar = document.querySelector('.loading-progress');
        const loadingText = document.querySelector('.loading-text');
        
        try {
            // Determine total assets to load - removed dome texture and golden armor
            this.totalAssets = 7; // 4 characters + 3 weapons
            
            // Create a fallback dome texture instead of trying to load it
            const canvas = document.createElement('canvas');
            canvas.width = 1024;
            canvas.height = 1024;
            const ctx = canvas.getContext('2d');
            
            // Create a sky gradient
            const gradient = ctx.createLinearGradient(0, 0, 0, 1024);
            gradient.addColorStop(0, '#1a237e'); // Deep blue
            gradient.addColorStop(0.4, '#3949ab'); // Medium blue
            gradient.addColorStop(0.8, '#5c6bc0'); // Light blue
            gradient.addColorStop(1, '#9fa8da'); // Very light blue
            
            ctx.fillStyle = gradient;
            ctx.fillRect(0, 0, 1024, 1024);
            
            const fallbackDomeTexture = new THREE.CanvasTexture(canvas);
            fallbackDomeTexture.mapping = THREE.EquirectangularReflectionMapping;
            fallbackDomeTexture.colorSpace = THREE.SRGBColorSpace;
            fallbackDomeTexture.needsUpdate = true;
            
            // Add to textures
            this.textures.environments = { dome: fallbackDomeTexture };
            this.loadedAssets++; // Count as loaded
            this.updateLoadingProgress(loadingProgressBar, loadingText);
            
            // Load character models
            const characterPromises = [
                this.loadModelCharacter('/assets/models/characters/barbarian.glb', 'characters.barbarian'),
                this.loadModelCharacter('/assets/models/characters/Knight.glb', 'characters.knight'),
                this.loadModelCharacter('/assets/models/characters/Mage.glb', 'characters.mage'),
                this.loadModelCharacter('/assets/models/characters/Rogue.glb', 'characters.rogue')
            ];
            
            // Load weapon models - removed gun.glb as we don't need it
            const weaponPromises = [
                this.loadModel('/assets/models/weapons/sword.glb', 'weapons.sword'),
                this.loadModel('/assets/models/weapons/axe.glb', 'weapons.axe'),
                this.loadModel('/assets/models/weapons/bow.glb', 'weapons.bow')
            ];
            
            // Create fallback skin texture instead of loading
            const skinCanvas = document.createElement('canvas');
            skinCanvas.width = 512;
            skinCanvas.height = 512;
            const skinCtx = skinCanvas.getContext('2d');
            
            // Create a golden gradient
            skinCtx.fillStyle = '#d4af37'; // Gold color
            skinCtx.fillRect(0, 0, 512, 512);
            
            // Add some texture patterns
            skinCtx.fillStyle = '#ffdf00'; // Brighter gold
            for (let i = 0; i < 50; i++) {
                const x = Math.random() * 512;
                const y = Math.random() * 512;
                const size = 5 + Math.random() * 15;
                skinCtx.fillRect(x, y, size, size);
            }
            
            const fallbackSkinTexture = new THREE.CanvasTexture(skinCanvas);
            fallbackSkinTexture.needsUpdate = true;
            
            // Add to textures
            this.textures.skins = { golden_armor: fallbackSkinTexture };
            this.loadedAssets++; // Count as loaded
            this.updateLoadingProgress(loadingProgressBar, loadingText);
            
            // Wait for all assets to load in parallel
            const results = await Promise.allSettled([
                ...characterPromises, 
                ...weaponPromises
            ]);
            
            // Check for any rejected promises and log them
            results.forEach((result, index) => {
                if (result.status === 'rejected') {
                    console.error(`Asset loading error at index ${index}:`, result.reason);
                }
            });
            
            console.log('AssetManager initialized successfully');
            return true;
        } catch (error) {
            console.error('Failed to initialize AssetManager:', error);
            console.error('Error stack:', error.stack);
            if (loadingText) {
                loadingText.textContent = 'Failed to load assets. Please refresh the page.';
            }
            throw error;
        }
    }
    
    /**
     * Update the loading progress UI
     * @param {HTMLElement} progressBar - The progress bar element
     * @param {HTMLElement} textElement - The text element to update
     */
    updateLoadingProgress(progressBar, textElement) {
        const progress = (this.loadedAssets / this.totalAssets) * 100;
        
        if (progressBar) {
            progressBar.style.width = `${progress}%`;
        }
        
        if (textElement) {
            textElement.textContent = `Loading assets... ${Math.round(progress)}%`;
        }
    }
    
    /**
     * Get a model by key, with fallback
     * @param {string} key - Asset key in format "category.subcategory.name"
     * @returns {Object} The requested model or a fallback
     */
    getModel(key) {
        // Parse the key path
        const parts = key.split('.');
        let result = this.models;
        
        for (const part of parts) {
            if (result && result[part] !== undefined) {
                result = result[part];
            } else {
                console.warn(`Model not found: ${key}, using fallback`);
                // Return a simple cube as fallback
                const geometry = new THREE.BoxGeometry(1, 1, 1);
                const material = this.getMaterial('default.' + parts[0]) || this.defaultMaterials.character;
                return new THREE.Mesh(geometry, material);
            }
        }
        
        return result || null;
    }
    
    /**
     * Get a texture by key, with fallback
     * @param {string} key - Asset key in format "category.name"
     * @returns {THREE.Texture} The requested texture or a fallback
     */
    getTexture(key) {
        // Parse the key path
        const parts = key.split('.');
        let result = this.textures;
        
        for (const part of parts) {
            if (result && result[part] !== undefined) {
                result = result[part];
            } else {
                console.warn(`Texture not found: ${key}, using fallback`);
                // Return a simple color texture as fallback
                const fallbackTexture = new THREE.Texture();
                fallbackTexture.needsUpdate = true;
                return fallbackTexture;
            }
        }
        
        return result || null;
    }
    
    /**
     * Get a material by key, with fallback
     * @param {string} key - Material key
     * @returns {THREE.Material} The requested material or a fallback
     */
    getMaterial(key) {
        return this.materials[key] || this.defaultMaterials.character;
    }
    
    /**
     * Load a texture from URL with error handling
     * @param {string} url - Texture URL
     * @param {string} key - Key to store the texture under
     * @returns {Promise} Resolves with the loaded texture
     */
    loadTexture(url, key) {
        return new Promise((resolve) => {
            try {
                this.textureLoader.load(
                    url,
                    (texture) => {
                        // Store and resolve
                        if (key) {
                            const parts = key.split('.');
                            let target = this.textures;
                            
                            // Create nested objects if they don't exist
                            for (let i = 0; i < parts.length - 1; i++) {
                                if (!target[parts[i]]) {
                                    target[parts[i]] = {};
                                }
                                target = target[parts[i]];
                            }
                            
                            // Set the texture at the final level
                            target[parts[parts.length - 1]] = texture;
                        }
                        
                        this.loadedAssets++;
                        this.updateLoadingProgress(
                            document.querySelector('.loading-progress'),
                            document.querySelector('.loading-text')
                        );
                        resolve(texture);
                    },
                    // Progress callback
                    (xhr) => {
                        const progress = (xhr.loaded / xhr.total) * 100;
                        console.log(`${key} ${Math.round(progress)}% loaded`);
                    },
                    // Error callback
                    (error) => {
                        console.error(`Error loading texture ${url}:`, error);
                        
                        // Create a fallback texture
                        const fallbackTexture = this.createFallbackTexture(key);
                        
                        // Store the fallback
                        if (key) {
                            const parts = key.split('.');
                            let target = this.textures;
                            
                            // Create nested objects if they don't exist
                            for (let i = 0; i < parts.length - 1; i++) {
                                if (!target[parts[i]]) {
                                    target[parts[i]] = {};
                                }
                                target = target[parts[i]];
                            }
                            
                            // Set the fallback texture
                            target[parts[parts.length - 1]] = fallbackTexture;
                        }
                        
                        this.loadedAssets++;
                        this.updateLoadingProgress(
                            document.querySelector('.loading-progress'),
                            document.querySelector('.loading-text')
                        );
                        
                        // Resolve with the fallback
                        resolve(fallbackTexture);
                    }
                );
            } catch (error) {
                console.error(`Exception loading texture ${url}:`, error);
                
                // Create and resolve with a fallback texture
                const fallbackTexture = this.createFallbackTexture(key);
                
                // Store the fallback
                if (key) {
                    const parts = key.split('.');
                    let target = this.textures;
                    
                    // Create nested objects if they don't exist
                    for (let i = 0; i < parts.length - 1; i++) {
                        if (!target[parts[i]]) {
                            target[parts[i]] = {};
                        }
                        target = target[parts[i]];
                    }
                    
                    // Set the fallback texture
                    target[parts[parts.length - 1]] = fallbackTexture;
                }
                
                this.loadedAssets++;
                this.updateLoadingProgress(
                    document.querySelector('.loading-progress'),
                    document.querySelector('.loading-text')
                );
                
                resolve(fallbackTexture);
            }
        });
    }
    
    /**
     * Create a fallback texture when loading fails
     * @param {string} key - The texture key
     * @returns {THREE.Texture} A fallback texture
     */
    createFallbackTexture(key) {
        // Create a canvas-based fallback texture
        const canvas = document.createElement('canvas');
        canvas.width = 256;
        canvas.height = 256;
        const ctx = canvas.getContext('2d');
        
        // Fill with a gradient based on the type of texture
        let gradient;
        if (key && key.includes('environments')) {
            // Skybox fallback - blue gradient for sky
            gradient = ctx.createLinearGradient(0, 0, 0, 256);
            gradient.addColorStop(0, '#000033');
            gradient.addColorStop(1, '#3366AA');
        } else if (key && key.includes('skins')) {
            // Character skin fallback
            gradient = ctx.createRadialGradient(128, 128, 0, 128, 128, 128);
            gradient.addColorStop(0, '#DDAA66');
            gradient.addColorStop(1, '#AA7744');
        } else {
            // Generic texture fallback - checkerboard pattern
            ctx.fillStyle = '#888888';
            ctx.fillRect(0, 0, 256, 256);
            ctx.fillStyle = '#444444';
            
            const gridSize = 32;
            for (let x = 0; x < 256; x += gridSize) {
                for (let y = 0; y < 256; y += gridSize) {
                    if ((x / gridSize + y / gridSize) % 2 === 0) {
                        ctx.fillRect(x, y, gridSize, gridSize);
                    }
                }
            }
            
            // Add a fallback label
            ctx.fillStyle = '#FF0000';
            ctx.font = '24px Arial';
            ctx.textAlign = 'center';
            ctx.fillText('FALLBACK', 128, 128);
            
            const texture = new THREE.CanvasTexture(canvas);
            texture.needsUpdate = true;
            return texture;
        }
        
        ctx.fillStyle = gradient;
        ctx.fillRect(0, 0, 256, 256);
        
        // Add a fallback label
        ctx.fillStyle = '#FFFFFF';
        ctx.font = '24px Arial';
        ctx.textAlign = 'center';
        const textureName = key ? key.split('.').pop() : 'texture';
        ctx.fillText(`FALLBACK ${textureName}`, 128, 128);
        
        const texture = new THREE.CanvasTexture(canvas);
        texture.needsUpdate = true;
        return texture;
    }
    
    /**
     * Load a GLTF model with error handling
     * @param {string} url - Model URL
     * @param {string} key - Key to store the model under
     * @returns {Promise} Resolves with the loaded model
     */
    loadModel(url, key) {
        return new Promise((resolve, reject) => {
            try {
                // Attempt to load the model
                this.gltfLoader.load(
                    url,
                    (gltf) => {
                        // Store and resolve
                        if (key) {
                            const parts = key.split('.');
                            let target = this.models;
                            
                            // Create nested objects if they don't exist
                            for (let i = 0; i < parts.length - 1; i++) {
                                if (!target[parts[i]]) {
                                    target[parts[i]] = {};
                                }
                                target = target[parts[i]];
                            }
                            
                            // Set the model at the final level
                            target[parts[parts.length - 1]] = gltf.scene;
                            
                            // Setup model properties
                            gltf.scene.traverse((child) => {
                                if (child.isMesh) {
                                    child.castShadow = true;
                                    child.receiveShadow = true;
                                }
                            });
                        }
                        
                        this.loadedAssets++;
                        this.updateLoadingProgress(
                            document.querySelector('.loading-progress'),
                            document.querySelector('.loading-text')
                        );
                        resolve(gltf.scene);
                    },
                    // Progress callback
                    (xhr) => {
                        const progress = (xhr.loaded / xhr.total) * 100;
                        console.log(`${key} ${Math.round(progress)}% loaded`);
                    },
                    // Error callback
                    (error) => {
                        console.error(`Error loading model ${url}:`, error);
                        
                        // Create a fallback model
                        const fallbackModel = this.createFallbackModel(key);
                        
                        // Store the fallback
                        if (key) {
                            const parts = key.split('.');
                            let target = this.models;
                            
                            // Create nested objects if they don't exist
                            for (let i = 0; i < parts.length - 1; i++) {
                                if (!target[parts[i]]) {
                                    target[parts[i]] = {};
                                }
                                target = target[parts[i]];
                            }
                            
                            // Set the fallback model
                            target[parts[parts.length - 1]] = fallbackModel;
                        }
                        
                        this.loadedAssets++;
                        this.updateLoadingProgress(
                            document.querySelector('.loading-progress'),
                            document.querySelector('.loading-text')
                        );
                        
                        // Resolve with the fallback instead of rejecting
                        resolve(fallbackModel);
                    }
                );
            } catch (error) {
                console.error(`Exception loading model ${url}:`, error);
                
                // Create and resolve with a fallback model
                const fallbackModel = this.createFallbackModel(key);
                resolve(fallbackModel);
                
                this.loadedAssets++;
                this.updateLoadingProgress(
                    document.querySelector('.loading-progress'),
                    document.querySelector('.loading-text')
                );
            }
        });
    }
    
    /**
     * Load a character GLTF model with error handling
     * @param {string} url - Model URL
     * @param {string} key - Key to store the model under
     * @returns {Promise} Resolves with the loaded model
     */
    loadModelCharacter(url, key) {
        return new Promise(async (resolve, reject) => {
            try {
                // First load the animations file
                console.log("Loading animations file for character models...");
                let animations = [];
                
                try {
                    const animationsGltf = await new Promise((resolveAnim, rejectAnim) => {
                        this.gltfLoader.load(
                            "/assets/models/characters/animations.glb",
                            resolveAnim,
                            null,
                            rejectAnim
                        );
                    });
                    
                    if (animationsGltf && animationsGltf.animations) {
                        animations = animationsGltf.animations;
                        console.log(`Loaded ${animations.length} animations from animations.glb`);
                    } else {
                        console.warn("No animations found in animations.glb");
                    }
                } catch (animError) {
                    console.error("Failed to load animations:", animError);
                }
                
                // Now load the character model
                console.log(`Loading character model: ${url}`);
                this.gltfLoader.load(
                    url,
                    (gltf) => {
                        // Store and resolve
                        if (key) {
                            const parts = key.split('.');
                            let target = this.models;
                            
                            // Create nested objects if they don't exist
                            for (let i = 0; i < parts.length - 1; i++) {
                                if (!target[parts[i]]) {
                                    target[parts[i]] = {};
                                }
                                target = target[parts[i]];
                            }
                            
                            // Set the model at the final level
                            target[parts[parts.length - 1]] = gltf.scene;
                            
                            // Assign the animations to the model
                            // Use model's own animations if available, otherwise use the global animations
                            if (gltf.animations && gltf.animations.length > 0) {
                                console.log(`Model has ${gltf.animations.length} embedded animations`);
                                target[parts[parts.length - 1]].animations = gltf.animations;
                            } else if (animations.length > 0) {
                                console.log(`Using ${animations.length} shared animations for the model`);
                                target[parts[parts.length - 1]].animations = animations;
                            } else {
                                console.warn("No animations available for model, creating dummy animation");
                                // Create a simple dummy animation
                                target[parts[parts.length - 1]].animations = [
                                    new THREE.AnimationClip('idle', 1, [
                                        new THREE.NumberKeyframeTrack('.position[y]', [0, 0.5, 1], [0, 0.1, 0])
                                    ])
                                ];
                            }
                            
                            // Setup model properties
                            gltf.scene.traverse((child) => {
                                if (child.isMesh) {
                                    child.castShadow = true;
                                    child.receiveShadow = true;
                                }
                            });
                        }
                        
                        this.loadedAssets++;
                        this.updateLoadingProgress(
                            document.querySelector('.loading-progress'),
                            document.querySelector('.loading-text')
                        );
                        resolve(gltf.scene);
                    },
                    // Progress callback
                    (xhr) => {
                        const progress = (xhr.loaded / xhr.total) * 100;
                        console.log(`${key} ${Math.round(progress)}% loaded`);
                    },
                    // Error callback
                    (error) => {
                        console.error(`Error loading model ${url}:`, error);
                        
                        // Create a fallback model
                        const fallbackModel = this.createFallbackModel(key);
                        
                        // Add animations to the fallback model
                        if (animations.length > 0) {
                            fallbackModel.animations = animations;
                        } else {
                            fallbackModel.animations = [
                                new THREE.AnimationClip('idle', 1, [
                                    new THREE.NumberKeyframeTrack('.position[y]', [0, 0.5, 1], [0, 0.1, 0])
                                ])
                            ];
                        }
                        
                        // Store the fallback
                        if (key) {
                            const parts = key.split('.');
                            let target = this.models;
                            
                            // Create nested objects if they don't exist
                            for (let i = 0; i < parts.length - 1; i++) {
                                if (!target[parts[i]]) {
                                    target[parts[i]] = {};
                                }
                                target = target[parts[i]];
                            }
                            
                            // Set the fallback model
                            target[parts[parts.length - 1]] = fallbackModel;
                        }
                        
                        this.loadedAssets++;
                        this.updateLoadingProgress(
                            document.querySelector('.loading-progress'),
                            document.querySelector('.loading-text')
                        );
                        
                        // Resolve with the fallback instead of rejecting
                        resolve(fallbackModel);
                    }
                );
            } catch (error) {
                console.error(`Exception loading model ${url}:`, error);
                
                // Create and resolve with a fallback model
                const fallbackModel = this.createFallbackModel(key);
                
                // Add a dummy animation to the fallback
                fallbackModel.animations = [
                    new THREE.AnimationClip('idle', 1, [
                        new THREE.NumberKeyframeTrack('.position[y]', [0, 0.5, 1], [0, 0.1, 0])
                    ])
                ];
                
                resolve(fallbackModel);
                
                this.loadedAssets++;
                this.updateLoadingProgress(
                    document.querySelector('.loading-progress'),
                    document.querySelector('.loading-text')
                );
            }
        });
    }
    
    /**
     * Create a fallback model when loading fails
     * @param {string} key - The model key (to determine what type of fallback)
     * @returns {THREE.Object3D} A fallback 3D object
     */
    createFallbackModel(key) {
        const fallbackModel = new THREE.Object3D();
        let mesh;
        
        // Create different fallbacks based on model type
        if (key.includes('character')) {
            // Character fallback - humanoid shape
            const bodyGeo = new THREE.CapsuleGeometry(0.5, 1, 4, 8);
            const headGeo = new THREE.SphereGeometry(0.25, 8, 8);
            const material = new THREE.MeshStandardMaterial({ 
                color: key.includes('barbarian') ? 0xAA3333 : 0x3333AA 
            });
            
            const body = new THREE.Mesh(bodyGeo, material);
            body.position.y = 0.5;
            
            const head = new THREE.Mesh(headGeo, material);
            head.position.y = 1.5;
            
            fallbackModel.add(body);
            fallbackModel.add(head);
        } 
        else if (key.includes('weapon')) {
            // Weapon fallback
            let geometry;
            
            if (key.includes('sword')) {
                geometry = new THREE.BoxGeometry(0.1, 1, 0.1);
            } else if (key.includes('axe')) {
                geometry = new THREE.BoxGeometry(0.5, 0.8, 0.1);
            } else if (key.includes('bow')) {
                geometry = new THREE.TorusGeometry(0.3, 0.05, 8, 16, Math.PI);
            } else {
                geometry = new THREE.BoxGeometry(0.3, 0.3, 0.3);
            }
            
            const material = new THREE.MeshStandardMaterial({ color: 0xCCCCCC });
            mesh = new THREE.Mesh(geometry, material);
            fallbackModel.add(mesh);
        }
        else {
            // Generic fallback
            const geometry = new THREE.BoxGeometry(1, 1, 1);
            const material = new THREE.MeshStandardMaterial({ color: 0xFFAA00 });
            mesh = new THREE.Mesh(geometry, material);
            fallbackModel.add(mesh);
        }
        
        // Add a name to the fallback for identification
        fallbackModel.name = `fallback-${key}`;
        
        return fallbackModel;
    }
} 